import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

const handleSubmit = (e) => {
  e.preventDefault();
  
  // WhatsApp'a yönlendir
  const message = `
Ad Soyad: ${formData.name}
E-posta: ${formData.email}
Telefon: ${formData.phone}
Şirket: ${formData.company}
Mesaj: ${formData.message}
  `;
  
  const whatsappUrl = `https://wa.me/905418284644?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank');
  
  toast({
    title: "WhatsApp'a Yönlendiriliyorsunuz!",
    description: "Mesajınız WhatsApp üzerinden gönderilecek.",
  });
  
  setFormData({ name: '', email: '', phone: '', company: '', message: '' });
};



  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 py-16 mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">
            İletişim
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Sorularınız, talepleriniz veya önerileriniz için bizimle iletişime geçin.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold mb-6">Bize Ulaşın</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">
                        Ad Soyad *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Adınız ve soyadınız"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">
                        E-posta *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="ornek@email.com"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">
                        Telefon
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+90 (5XX) XXX XX XX"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">
                        Şirket Adı
                      </label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Şirket adınız"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Mesajınız *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Mesajınızı buraya yazın..."
                      className="w-full resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#E31E24] hover:bg-[#C01A1F] text-white py-6 text-lg rounded-lg"
                  >
                    Mesaj Gönder
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold mb-6">İletişim Bilgileri</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-[#E31E24]" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Telefon</div>
                      <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-[#E31E24] transition-colors">
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-[#E31E24]" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">E-posta</div>
                      <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-[#E31E24] transition-colors break-all">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#E31E24]" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Adres</div>
                      <p className="text-gray-600">{contactInfo.address}</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#E31E24]" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Çalışma Saatleri</div>
                      <p className="text-gray-600">{contactInfo.workingHours}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Map */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-64 bg-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3011.234!2d28.828333!3d41.009639!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNDHCsDAwJzM0LjciTiAyOMKwNDknNDIuMCJF!5e0!3m2!1str!2str!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="KargoPack Lokasyon"
                />
              </div>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-[#E31E24] to-[#C01A1F] rounded-3xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Toplu Sipariş İçin Özel Fiyat Teklifi</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Büyük miktarlarda sipariş vermek için özel fiyat teklifimizden yararlanabilirsiniz. Detaylı bilgi için satış ekibimizle iletişime geçin.
          </p>
          <Button size="lg" className="bg-white text-[#E31E24] hover:bg-gray-100 rounded-lg px-8 py-6 text-lg">
            Hemen Ara: {contactInfo.phone}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Contact;
